/**
 * Sprint Users-Nav — scalable Users page.
 * Server-side search/filter/sort/pagination via GET /api/admin/users?page=…
 * All state (q, role, company, status, sort, order, page, size) persists in
 * the URL. Any search/filter/sort change resets to page 1 (founder rule).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Plus, Search, Mail, Phone, MessagesSquare, ChevronLeft, ChevronRight, FilterX, KeyRound, FileSpreadsheet, Copy } from 'lucide-react';
import { OrgImportWizard } from '@/components/client/OrgImportWizard';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SyncedHScroll } from '@/components/shared/SyncedHScroll';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { useDataLabel } from '@/lib/dataI18n';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { ListGridToggle } from '@/components/shared/ListGridToggle';
import UserResponsesDialog from '@/components/admin/UserResponsesDialog';
import UserDetailDialog from '@/components/admin/UserDetailDialog';

const initials = (name) => (name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const ROLE_OPTIONS = [
  { value: 'evaluated',    labelKey: 'pages.users.roles.evaluated' },
  { value: 'hm_advisor',   labelKey: 'pages.users.roles.hm_advisor' },
  { value: 'client_admin', labelKey: 'pages.users.roles.client_admin' },
  { value: 'hm_admin',     labelKey: 'pages.users.roles.hm_admin' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAGE_SIZES = [25, 50, 100];

const ACCESS_TONE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  first_login_pending: 'bg-amber-50 text-amber-700 border-amber-200',
  password_change_pending: 'bg-amber-50 text-amber-700 border-amber-200',
  not_activated: 'bg-slate-100 text-slate-600 border-slate-200',
  disabled: 'bg-red-50 text-red-700 border-red-200',
};
const ACCESS_DEFAULTS = {
  active: 'Access: active', first_login_pending: 'First login pending',
  password_change_pending: 'Password change pending',
  not_activated: 'Not activated', disabled: 'Access disabled',
};

export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: authUser } = useApp();
  const tData = useDataLabel();
  const isAdmin = authUser?.role === 'hm_admin';
  const isAdvisor = authUser?.role === 'hm_advisor';
  const canCreateUser = isAdmin || isAdvisor;
  const [creds, setCreds] = useState(null);          // {email, password} one-time credentials dialog
  const [importOpen, setImportOpen] = useState(false);
  const [resetting, setResetting] = useState(null);

  const resettingRef = React.useRef(false);
  const doResetPassword = async (u) => {
    if (resettingRef.current) return;  // guard: double-click would rotate the password twice
    resettingRef.current = true;
    setResetting(u.id);
    try {
      const res = await adminApi.resetUserPassword(u.id);
      setCreds({ email: res.email, password: res.temp_password });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally { resettingRef.current = false; setResetting(null); }
  };
  // UAT — Advisors may only create client_admin / evaluated (backend enforces
  // this too; UI mirrors the policy so the caller can't pick a forbidden role).
  const roleOptions = useMemo(
    () => (isAdvisor
      ? ROLE_OPTIONS.filter(o => o.value === 'evaluated' || o.value === 'client_admin')
      : ROLE_OPTIONS),
    [isAdvisor],
  );

  // ---------- URL-persisted list state (Sprint Users-Nav) ----------
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const roleFilter = searchParams.get('role') || 'all';
  const companyFilter = searchParams.get('company') || 'all';
  const statusFilter = searchParams.get('status') || 'all';
  const sortKey = searchParams.get('sort') || '';
  const sortOrder = searchParams.get('order') === 'desc' ? 'desc' : 'asc';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const sizeRaw = parseInt(searchParams.get('size') || '25', 10);
  const pageSize = PAGE_SIZES.includes(sizeRaw) ? sizeRaw : 25;

  const updateParams = (patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '' || v === 'all' || (k === 'page' && String(v) === '1')) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  };

  // Sort adapter for SortableHeader (URL-backed; resets to page 1).
  const sort = {
    is: (k) => sortKey === k,
    dirOf: (k) => (sortKey === k ? sortOrder : null),
    toggle: (k) => {
      if (sortKey !== k) updateParams({ sort: k, order: 'asc', page: null });
      else if (sortOrder === 'asc') updateParams({ order: 'desc', page: null });
      else updateParams({ sort: null, order: null, page: null });
    },
  };

  // Debounced search box → URL (resets page).
  const [qInput, setQInput] = useState(q);
  useEffect(() => { setQInput(q); }, [q]);
  useEffect(() => {
    const h = setTimeout(() => { if (qInput !== q) updateParams({ q: qInput, page: null }); }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  // ---------- Data ----------
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25, total_pages: 0 });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const reload = () => setTick((n) => n + 1);

  useEffect(() => {
    adminApi.listCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (q) params.q = q;
    if (roleFilter !== 'all') params.role = roleFilter;
    if (companyFilter !== 'all') params.company_id = companyFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (sortKey) { params.sort = sortKey; params.order = sortOrder; }
    adminApi.listUsers(params)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) toast.error(t('pages.users.loadError', { defaultValue: 'Failed to load users' })); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tick]);

  const users = data.items || [];
  const total = data.total || 0;
  const totalPages = data.total_pages || 0;
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);
  const hasActiveFilters = !!q || roleFilter !== 'all' || companyFilter !== 'all' || statusFilter !== 'all';
  const clearFilters = () => { setQInput(''); updateParams({ q: null, role: null, company: null, status: null, page: null }); };

  // Per-user response viewer dialog
  const [respOpen, setRespOpen] = useState(false);
  const [respUser, setRespUser] = useState(null);
  // Detail dialog (opens on row / card click)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const openDetail = (u) => { setDetailUser(u); setDetailOpen(true); };

  // Add User modal state
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: isAdvisor ? 'evaluated' : '', company_id: '__none__', department_id: '__none__', position: '' });
  // UAT — internal CTH roles carry no tenant binding: company/department are
  // hidden and forced to none for hm_advisor / hm_admin creations.
  const isInternalRole = form.role === 'hm_advisor' || form.role === 'hm_admin';
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // BUG 10 — 2-step Department → Position picker. Departments are fetched
  // when a company is selected and carry their seeded position list.
  const [departments, setDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);

  // Re-fetch departments whenever a company is selected. Reset cascading
  // selections so a stale department/position can't leak across companies.
  useEffect(() => {
    const cid = form.company_id;
    if (!cid || cid === '__none__') { setDepartments([]); return; }
    let cancelled = false;
    setDeptLoading(true);
    adminApi.listDepartments(cid)
      .then((items) => { if (!cancelled) setDepartments(items || []); })
      .catch(() => { if (!cancelled) setDepartments([]); })
      .finally(() => { if (!cancelled) setDeptLoading(false); });
    return () => { cancelled = true; };
  }, [form.company_id]);

  const selectedDept = useMemo(
    () => departments.find((d) => d.id === form.department_id),
    [departments, form.department_id],
  );

  // Grid/list toggle
  const [view, setView] = useState('list');

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('pages.users.errName', { defaultValue: 'Name is required' });
    if (!form.email.trim()) e.email = t('pages.users.errEmailReq', { defaultValue: 'Email is required' });
    else if (!EMAIL_RE.test(form.email.trim())) e.email = t('pages.users.errEmailFmt', { defaultValue: 'Invalid email format' });
    if (!form.role) e.role = t('pages.users.errRole', { defaultValue: 'Role is required' });
    // UAT — client-bound roles (evaluated / client_admin) always need a company;
    // internal CTH roles never do. Advisors are book-scoped (client-bound only).
    const clientBound = form.role === 'evaluated' || form.role === 'client_admin';
    if (!isInternalRole && (isAdvisor || clientBound) && (!form.company_id || form.company_id === '__none__')) {
      e.company_id = t('pages.users.errCompanyReq', { defaultValue: 'Company is required' });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
      };
      if (form.phone.trim()) body.phone = form.phone.trim();
      if (form.company_id && form.company_id !== '__none__') body.company_id = form.company_id;
      if (form.department_id && form.department_id !== '__none__') body.department_id = form.department_id;
      if (form.position.trim()) body.position = form.position.trim();
      const res = await adminApi.createUser(body);
      toast.success(t('pages.users.createdToast', { defaultValue: 'User created' }));
      const tempPw = res.temp_password || res.initial_password_hint;
      if (tempPw) {
        setCreds({ email: body.email, password: tempPw });
      }
      setOpen(false);
      setForm({ name: '', email: '', phone: '', role: isAdvisor ? 'evaluated' : 'consultant', company_id: '__none__', department_id: '__none__', position: '' });
      setErrors({});
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Save failed';
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const emptyState = (
    <div className="py-8 text-center space-y-2" data-testid={hasActiveFilters ? 'users-empty-filtered' : 'users-empty-none'}>
      <p className="text-sm text-muted-foreground italic">
        {hasActiveFilters
          ? t('pages.users.emptyNoMatch', { defaultValue: 'No users match the current filters.' })
          : t('pages.users.emptyNoUsers', { defaultValue: 'No users exist yet — create the first one with “Add user”.' })}
      </p>
      {hasActiveFilters && (
        <Button variant="outline" size="sm" onClick={clearFilters} data-testid="users-clear-filters">
          <FilterX className="h-3.5 w-3.5 mr-1.5" />{t('pages.users.clearFilters', { defaultValue: 'Clear filters' })}
        </Button>
      )}
    </div>
  );

  const paginationBar = (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid="users-pagination">
      <p className="text-xs text-muted-foreground tabular-nums" data-testid="users-page-info">
        {t('pages.users.showingRange', { defaultValue: 'Showing {{from}}–{{to}} of {{total}}', from: rangeFrom, to: rangeTo, total })}
        {' · '}
        {t('pages.users.pageOf', { defaultValue: 'Page {{page}} of {{pages}}', page: Math.min(page, Math.max(totalPages, 1)), pages: Math.max(totalPages, 1) })}
      </p>
      <div className="flex items-center gap-2">
        <Select value={String(pageSize)} onValueChange={(v) => updateParams({ size: v === '25' ? null : v, page: null })}>
          <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="users-page-size"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((n) => (
              <SelectItem key={n} value={String(n)} data-testid={`users-page-size-${n}`}>
                {n} {t('pages.users.perPage', { defaultValue: 'per page' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline" size="sm" className="h-8"
          disabled={page <= 1 || loading}
          onClick={() => updateParams({ page: page - 1 })}
          data-testid="users-page-prev"
        >
          <ChevronLeft className="h-3.5 w-3.5 mr-1" />{t('pages.users.prevPage', { defaultValue: 'Previous' })}
        </Button>
        <Button
          variant="outline" size="sm" className="h-8"
          disabled={page >= totalPages || loading}
          onClick={() => updateParams({ page: page + 1 })}
          data-testid="users-page-next"
        >
          {t('pages.users.nextPage', { defaultValue: 'Next' })}<ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Topbar
        title={t('pages.users.title')}
        subtitle={t('pages.users.subtitle')}
        actions={
          canCreateUser && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid="users-bulk-import-btn" onClick={() => setImportOpen(true)} className="gap-1.5">
                <FileSpreadsheet className="h-4 w-4" /> {t('pages.collaborators.import.btn')}
              </Button>
              <Button size="sm" data-testid="users-add-btn" onClick={() => setOpen(true)} className="bg-gradient-primary hover:opacity-90 gap-1.5">
                <Plus className="h-4 w-4" /> {t('pages.users.addUser')}
              </Button>
            </div>
          )
        }
      />
      <OrgImportWizard mode="hm" open={importOpen} onClose={() => setImportOpen(false)} onImported={reload} />
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent className="max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" />{t('pages.users.credsTitle')}</DialogTitle>
            <DialogDescription>
              {t('pages.users.credsNote')}{' '}
              {t('pages.users.credsRotates', { defaultValue: 'Generating new credentials replaces any previously issued temporary password.' })}
            </DialogDescription>
          </DialogHeader>
          {creds && (
            <div className="space-y-2 text-sm">
              <div className="rounded-lg border border-border/60 p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t('pages.users.credsEmail')}</p>
                  <p className="font-medium truncate" data-testid="creds-email">{creds.email}</p>
                </div>
              </div>
              <div className="rounded-lg border border-border/60 p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t('pages.users.credsPassword')}</p>
                  <p className="font-mono font-semibold" data-testid="creds-password">{creds.password}</p>
                </div>
                <Button
                  variant="outline" size="sm" className="gap-1.5 shrink-0" data-testid="creds-copy-btn"
                  onClick={() => { navigator.clipboard?.writeText(creds.password); toast.success(t('pages.users.credsCopied')); }}
                >
                  <Copy className="h-3.5 w-3.5" /> {t('pages.users.credsCopy')}
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreds(null)} data-testid="creds-close-btn">{t('pages.users.credsDone')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="p-6 lg:p-8 space-y-5">
        <Card className="p-4 shadow-card flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="users-search"
              placeholder={t('pages.users.searchPh')}
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <Select value={roleFilter} onValueChange={(v) => updateParams({ role: v, page: null })}>
            <SelectTrigger className="h-10 w-40" data-testid="users-filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pages.users.allRoles', { defaultValue: 'All roles' })}</SelectItem>
              {roleOptions.map(o => (
                <SelectItem key={o.value} value={o.value} data-testid={`users-filter-role-${o.value}`}>{t(o.labelKey)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={companyFilter} onValueChange={(v) => updateParams({ company: v, page: null })}>
            <SelectTrigger className="h-10 w-44" data-testid="users-filter-company"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pages.users.allCompanies', { defaultValue: 'All clients' })}</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => updateParams({ status: v, page: null })}>
            <SelectTrigger className="h-10 w-40" data-testid="users-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pages.users.allStatuses', { defaultValue: 'All statuses' })}</SelectItem>
              <SelectItem value="active" data-testid="users-filter-status-active">{t('pages.users.statusActive', { defaultValue: 'Active' })}</SelectItem>
              <SelectItem value="inactive" data-testid="users-filter-status-inactive">{t('pages.users.statusInactive', { defaultValue: 'Inactive' })}</SelectItem>
            </SelectContent>
          </Select>
          <ListGridToggle value={view} onChange={setView} testIdPrefix="users-view" />
        </Card>

        {view === 'grid' ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
              {loading && [...Array(6)].map((_, i) => (
                <Card key={i} className="p-5 shadow-card">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="flex-1 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /></div>
                  </div>
                </Card>
              ))}
              {!loading && users.map(u => (
                <Card
                  key={u.id}
                  className="p-5 shadow-card hover:shadow-medium transition-smooth cursor-pointer"
                  data-testid={`user-card-${u.id}`}
                  onClick={() => openDetail(u)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(u); } }}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-12 w-12"><AvatarFallback className="bg-gradient-primary text-white text-sm font-bold">{initials(u.name)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge variant="outline" className="text-xs">{t(`pages.users.roles.${u.role}`, { defaultValue: u.role })}</Badge>
                        <Badge variant="outline" className="text-xs">{t(`common.statusValues.${u.status || 'active'}`, { defaultValue: u.status || 'active' })}</Badge>
                      </div>
                      {u.company_name && <p className="text-xs text-muted-foreground mt-2 truncate">{tData(u.company_name)}</p>}
                    </div>
                  </div>
                </Card>
              ))}
              {!loading && !users.length && <div className="col-span-full">{emptyState}</div>}
            </div>
            {paginationBar}
          </>
        ) : (
        <>
        <Card className="shadow-card">
          <SyncedHScroll testId="users-table-scroll">
          <Table containerClassName="!overflow-visible">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead><SortableHeader sort={sort} k="name" testIdPrefix="users">{t('pages.users.user')}</SortableHeader></TableHead>
                <TableHead><SortableHeader sort={sort} k="role" testIdPrefix="users">{t('pages.users.role')}</SortableHeader></TableHead>
                <TableHead className="hidden md:table-cell min-w-[160px]"><SortableHeader sort={sort} k="company" testIdPrefix="users">{t('pages.users.company')}</SortableHeader></TableHead>
                <TableHead className="hidden md:table-cell">{t('pages.users.contact')}</TableHead>
                <TableHead className="min-w-[170px]"><SortableHeader sort={sort} k="status" testIdPrefix="users">{t('pages.users.status')}</SortableHeader></TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><div className="flex items-center gap-3"><Skeleton className="h-9 w-9 rounded-full" /><div className="space-y-1.5"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3 w-40" /></div></div></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-36" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-14" /></TableCell>
                  <TableCell />
                </TableRow>
              ))}
              {!loading && users.map(u => (
                <TableRow
                  key={u.id}
                  className="hover:bg-muted/30 cursor-pointer"
                  data-testid={`user-row-${u.id}`}
                  onClick={() => openDetail(u)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9"><AvatarFallback className="bg-gradient-primary text-white text-xs font-semibold">{initials(u.name)}</AvatarFallback></Avatar>
                      <div className="min-w-0"><p className="text-sm font-semibold truncate">{u.name}</p><p className="text-xs text-muted-foreground truncate">{u.email}</p></div>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="font-medium">{t(`pages.users.roles.${u.role}`, { defaultValue: u.role })}</Badge></TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{u.company_name ? tData(u.company_name) : <span className="text-muted-foreground">{t('pages.users.internal')}</span>}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" />{u.email}</div>
                      {u.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{u.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <Badge variant="outline">{t(`common.statusValues.${u.status || 'active'}`, { defaultValue: u.status || 'active' })}</Badge>
                      {u.access_state && (
                        <Badge variant="outline" className={`text-xs ${ACCESS_TONE[u.access_state] || ''}`} data-testid={`user-access-${u.id}`}>
                          {t(`pages.users.access.${u.access_state}`, { defaultValue: ACCESS_DEFAULTS[u.access_state] })}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        data-testid={`user-row-view-responses-${u.id}`}
                        title={t('pages.users.viewResponses')}
                        onClick={() => { setRespUser(u); setRespOpen(true); }}
                      >
                        <MessagesSquare className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`user-row-reset-pw-${u.id}`}
                          title={t('pages.users.resetPw')}
                          disabled={resetting === u.id}
                          onClick={() => doResetPassword(u)}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !users.length && (
                <TableRow><TableCell colSpan={6}>{emptyState}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          </SyncedHScroll>
        </Card>
        {paginationBar}
        </>
        )}
      </div>

      {/* Add User dialog (Prompt 11) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg lg:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.users.createUserTitle')}</DialogTitle>
            <DialogDescription>{t('pages.users.createUserDesc')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2 sm:grid-cols-2 [&>div]:min-w-0 [&_button[role=combobox]>span]:truncate [&_button[role=combobox]]:max-w-full [&_button[role=combobox]]:w-full">
            <div>
              <Label className="text-xs">{t('pages.users.fullName', { defaultValue: 'Full name' })} *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('pages.users.nameSamplePh')} data-testid="add-user-name" />
              {errors.name && <p className="text-xs text-destructive mt-1" data-testid="add-user-name-err">{errors.name}</p>}
            </div>
            <div>
              <Label className="text-xs">{t('pages.users.email', { defaultValue: 'Email' })} *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder={t('pages.users.emailSamplePh')} data-testid="add-user-email" />
              {errors.email && <p className="text-xs text-destructive mt-1" data-testid="add-user-email-err">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-xs">{t('pages.users.phoneOpt', { defaultValue: 'Phone (optional)' })}</Label>
              <Input type="tel" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder={t('pages.users.phonePlaceholder', { defaultValue: '+52 55 1234 5678' })} data-testid="add-user-phone" />
            </div>
            <div>
              <Label className="text-xs">{t('pages.users.role', { defaultValue: 'Role' })} *</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm(f => (
                  (v === 'hm_advisor' || v === 'hm_admin')
                    ? { ...f, role: v, company_id: '__none__', department_id: '__none__' }
                    : { ...f, role: v }
                ))}
              >
                <SelectTrigger data-testid="add-user-role">
                  <SelectValue placeholder={t('pages.users.selectRolePh', { defaultValue: 'Select a role' })} />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map(o => (
                    <SelectItem key={o.value} value={o.value} data-testid={`add-user-role-${o.value}`}>
                      {t(o.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.role && <p className="text-xs text-destructive mt-1" data-testid="add-user-role-err">{errors.role}</p>}
            </div>
            {!isInternalRole && (
            <div>
              <Label className="text-xs">
                {isAdvisor
                  ? `${t('pages.users.company', { defaultValue: 'Client / company' })} *`
                  : t('pages.users.companyOpt', { defaultValue: 'Client / company (optional)' })}
              </Label>
              <Select value={form.company_id} onValueChange={(v) => setForm(f => ({ ...f, company_id: v, department_id: '__none__', position: '' }))}>
                <SelectTrigger data-testid="add-user-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!isAdvisor && (
                    <SelectItem value="__none__">{t('pages.users.noCompany', { defaultValue: '— None (internal)' })}</SelectItem>
                  )}
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.company_id && <p className="text-xs text-destructive mt-1" data-testid="add-user-company-err">{errors.company_id}</p>}
            </div>
            )}
            {/* BUG 10 — Two-step Department → Position picker. Only shown
                 when a company has been chosen. Positions are filtered to
                 the seeded list on the selected department. */}
            {!isInternalRole && form.company_id !== '__none__' && (
              <>
                <div>
                  <Label className="text-xs">{t('pages.users.departmentOpt', { defaultValue: 'Department (optional)' })}</Label>
                  <Select
                    value={form.department_id}
                    onValueChange={(v) => setForm(f => ({ ...f, department_id: v, position: '' }))}
                    disabled={deptLoading}
                  >
                    <SelectTrigger data-testid="add-user-department"><SelectValue placeholder={deptLoading ? t('common.loading') : undefined} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('pages.users.noDepartment', { defaultValue: '— None' })}</SelectItem>
                      {departments.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{tData(d)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!deptLoading && !departments.length && (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="add-user-department-empty">
                      {t('pages.users.noDepartmentsHint', { defaultValue: 'This client has no departments yet — add them in the client profile.' })}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">{t('pages.users.positionOpt', { defaultValue: 'Position (optional)' })}</Label>
                  {selectedDept && (selectedDept.positions || []).length > 0 ? (
                    <Select
                      value={form.position || '__none__'}
                      onValueChange={(v) => setForm(f => ({ ...f, position: v === '__none__' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="add-user-position"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('pages.users.noPosition', { defaultValue: '— None' })}</SelectItem>
                        {selectedDept.positions.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={form.position}
                      onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))}
                      placeholder={t('pages.users.positionPlaceholder', { defaultValue: 'Senior Consultant' })}
                      data-testid="add-user-position"
                    />
                  )}
                </div>
              </>
            )}
            {form.company_id === '__none__' && (
              <div>
                <Label className="text-xs">{t('pages.users.positionOpt', { defaultValue: 'Position (optional)' })}</Label>
                <Input
                  value={form.position}
                  onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))}
                  placeholder={t('pages.users.positionSamplePh')}
                  data-testid="add-user-position"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="add-user-cancel">{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={submit} disabled={saving} data-testid="add-user-submit">
              {saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('pages.users.createUser', { defaultValue: 'Create user' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UserResponsesDialog open={respOpen} onOpenChange={setRespOpen} user={respUser} />
      <UserDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        user={detailUser}
        companies={companies}
        onViewResponses={(u) => { setRespUser(u); setRespOpen(true); }}
        onIssueCredentials={(u) => { setDetailOpen(false); doResetPassword(u); }}
        onChanged={reload}
      />
    </>
  );
}
